<div class="organizations-dashboard">
  <!-- Header -->
  <div class="dashboard-header">
    <div class="header-left">
      <h1><i class="fa-solid fa-building"></i> Organizations</h1>
      <span class="count-badge">{{ organizations.length }} total</span>
    </div>
    <div class="header-right">
      <button class="add-btn" (click)="startCreate()" [disabled]="isSaving">
        <i class="fa-solid fa-plus"></i>
        Add Organization
      </button>
    </div>
  </div>

  <!-- Loading State -->
  <div class="loading-state" *ngIf="isLoading">
    <i class="fa-solid fa-spinner fa-spin"></i>
    <span>Loading organizations...</span>
  </div>

  <!-- Error Message -->
  <div class="error-banner" *ngIf="errorMessage">
    <i class="fa-solid fa-exclamation-triangle"></i>
    {{ errorMessage }}
    <button class="close-btn" (click)="errorMessage = ''">
      <i class="fa-solid fa-times"></i>
    </button>
  </div>

  <!-- Success Message -->
  <div class="success-banner" *ngIf="successMessage">
    <i class="fa-solid fa-check-circle"></i>
    {{ successMessage }}
  </div>

  <!-- Main Content -->
  <div class="dashboard-content" *ngIf="!isLoading">
    <!-- Organizations List Panel -->
    <div class="org-list-panel">
      <!-- Search & Controls -->
      <div class="list-header">
        <div class="search-box">
          <i class="fa-solid fa-search"></i>
          <input
            type="text"
            placeholder="Search organizations..."
            [(ngModel)]="searchQuery"
          />
          <button class="clear-btn" *ngIf="searchQuery" (click)="searchQuery = ''">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        <div class="view-controls">
          <div class="view-toggle">
            <button
              class="view-btn"
              [class.active]="viewMode === 'tree'"
              (click)="viewMode = 'tree'"
              title="Tree view"
            >
              <i class="fa-solid fa-folder-tree"></i>
            </button>
            <button
              class="view-btn"
              [class.active]="viewMode === 'flat'"
              (click)="viewMode = 'flat'"
              title="Flat list"
            >
              <i class="fa-solid fa-list"></i>
            </button>
          </div>
          <div class="tree-controls" *ngIf="viewMode === 'tree'">
            <button class="tree-ctrl-btn" (click)="expandAll()" title="Expand all">
              <i class="fa-solid fa-expand"></i>
            </button>
            <button class="tree-ctrl-btn" (click)="collapseAll()" title="Collapse all">
              <i class="fa-solid fa-compress"></i>
            </button>
          </div>
          <div class="sort-controls" *ngIf="viewMode === 'flat'">
            <button
              class="sort-btn"
              [class.active]="sortBy === 'name'"
              (click)="setSortBy('name')"
            >
              Name <i class="fa-solid {{ getSortIcon('name') }}"></i>
            </button>
            <button
              class="sort-btn"
              [class.active]="sortBy === 'status'"
              (click)="setSortBy('status')"
            >
              Status <i class="fa-solid {{ getSortIcon('status') }}"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Organization Tree View -->
      <div class="org-list" *ngIf="viewMode === 'tree'">
        <div class="empty-state" *ngIf="orgTree.length === 0">
          <i class="fa-solid fa-building"></i>
          <p *ngIf="searchQuery">No organizations match your search</p>
          <p *ngIf="!searchQuery">No organizations yet</p>
          <button class="add-btn" *ngIf="!searchQuery" (click)="startCreate()">
            <i class="fa-solid fa-plus"></i>
            Add your first organization
          </button>
        </div>

        <!-- Tree nodes -->
        <ng-container *ngFor="let node of flattenedTreeNodes">
          <div
            class="org-tree-item"
            [class.selected]="selectedOrganization?.ID === node.org.ID"
            [style.padding-left.px]="16 + (node.depth * 24)"
            (click)="selectOrganization(node.org)"
          >
            <!-- Tree connector lines -->
            <div class="tree-connector" *ngIf="node.depth > 0">
              <span class="connector-line" [class.last-child]="isLastChild(node)"></span>
            </div>

            <!-- Expand/collapse toggle -->
            <button
              class="expand-toggle"
              *ngIf="node.hasChildren"
              (click)="toggleExpand(node, $event)"
            >
              <i class="fa-solid" [class.fa-chevron-down]="node.expanded" [class.fa-chevron-right]="!node.expanded"></i>
            </button>
            <span class="expand-placeholder" *ngIf="!node.hasChildren"></span>

            <!-- Org avatar or logo -->
            <div class="org-avatar" [class.has-children]="node.hasChildren" *ngIf="!getLogoURL(node.org)">
              {{ getInitials(node.org.Name) }}
            </div>
            <div class="org-logo" [class.has-children]="node.hasChildren" *ngIf="getLogoURL(node.org)">
              <img [src]="getLogoURL(node.org)" [alt]="node.org.Name">
            </div>

            <!-- Org info -->
            <div class="org-info">
              <h3>{{ node.org.Name }}</h3>
              <div class="org-meta">
                <span class="channel-count" *ngIf="getChannelCount(node.org) > 0">
                  <i class="fa-solid fa-broadcast-tower"></i>
                  {{ getChannelCount(node.org) }} channel{{ getChannelCount(node.org) > 1 ? 's' : '' }}
                </span>
                <span class="sub-org-count" *ngIf="node.hasChildren">
                  <i class="fa-solid fa-sitemap"></i>
                  {{ getDescendantCount(node) }} sub-org{{ getDescendantCount(node) > 1 ? 's' : '' }}
                </span>
              </div>
            </div>

            <!-- Quick actions -->
            <div class="tree-item-actions">
              <button
                class="action-icon-btn"
                (click)="startCreateSubOrg(node.org); $event.stopPropagation()"
                title="Add sub-organization"
              >
                <i class="fa-solid fa-plus"></i>
              </button>
              <button
                class="action-icon-btn"
                (click)="navigateToSettings(node.org); $event.stopPropagation()"
                title="Configure settings"
              >
                <i class="fa-solid fa-cog"></i>
              </button>
            </div>
          </div>
        </ng-container>
      </div>

      <!-- Flat List View (original) -->
      <div class="org-list" *ngIf="viewMode === 'flat'">
        <div class="empty-state" *ngIf="displayOrganizations.length === 0">
          <i class="fa-solid fa-building"></i>
          <p *ngIf="searchQuery">No organizations match your search</p>
          <p *ngIf="!searchQuery">No organizations yet</p>
          <button class="add-btn" *ngIf="!searchQuery" (click)="startCreate()">
            <i class="fa-solid fa-plus"></i>
            Add your first organization
          </button>
        </div>

        <div
          class="org-card"
          *ngFor="let org of displayOrganizations"
          [class.selected]="selectedOrganization?.ID === org.ID"
          (click)="selectOrganization(org)"
        >
          <div class="org-avatar" *ngIf="!getLogoURL(org)">
            {{ getInitials(org.Name) }}
          </div>
          <div class="org-logo" *ngIf="getLogoURL(org)">
            <img [src]="getLogoURL(org)" [alt]="org.Name">
          </div>
          <div class="org-info">
            <h3>{{ org.Name }}</h3>
            <p class="parent-info" *ngIf="getParentName(org)">
              <i class="fa-solid fa-level-up-alt"></i>
              {{ getParentName(org) }}
            </p>
            <p class="status" *ngIf="org.Status && !getParentName(org)">
              <i class="fa-solid fa-circle-info"></i>
              {{ org.Status }}
            </p>
            <p class="description" *ngIf="org.Description && !org.Status && !getParentName(org)">
              {{ org.Description | slice:0:60 }}{{ org.Description.length > 60 ? '...' : '' }}
            </p>
          </div>
          <button class="settings-btn" (click)="navigateToSettings(org); $event.stopPropagation()" title="Configure settings">
            <i class="fa-solid fa-cog"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Details Panel -->
    <div class="details-panel">
      <!-- Create Form -->
      <div class="edit-form" *ngIf="isCreating">
        <div class="form-header">
          <h2>
            <i class="fa-solid fa-plus"></i>
            {{ editForm.parentId ? 'New Sub-Organization' : 'New Organization' }}
          </h2>
        </div>
        <div class="form-body">
          <!-- Parent org indicator when creating sub-org -->
          <div class="parent-indicator" *ngIf="editForm.parentId">
            <label>Creating under:</label>
            <div class="parent-org-display">
              <i class="fa-solid fa-building"></i>
              <span>{{ getParentOrgName(editForm.parentId) }}</span>
            </div>
          </div>

          <div class="form-group">
            <label>Name <span class="required">*</span></label>
            <input
              type="text"
              [(ngModel)]="editForm.name"
              placeholder="Organization name"
              [disabled]="isSaving"
            />
          </div>
          <div class="form-group">
            <label>Description</label>
            <textarea
              [(ngModel)]="editForm.description"
              placeholder="Brief description of the organization..."
              rows="4"
              [disabled]="isSaving"
            ></textarea>
          </div>
          <div class="form-group">
            <label>Parent Organization</label>
            <select [(ngModel)]="editForm.parentId" [disabled]="isSaving" class="parent-select">
              <option [ngValue]="null">None (top-level organization)</option>
              <optgroup label="Organizations">
                <option *ngFor="let org of organizations" [ngValue]="org.ID">
                  {{ getOrgPathString(org) }}
                </option>
              </optgroup>
            </select>
          </div>
          <div class="form-group">
            <label>Logo</label>
            <div class="logo-input-group">
              <!-- Logo Preview -->
              <div class="logo-preview" *ngIf="editForm.logoURL && !logoPreviewError">
                <img [src]="editForm.logoURL" alt="Logo preview" (error)="onLogoPreviewError()">
                <button class="clear-logo-btn" type="button" (click)="clearLogo()" title="Remove logo">
                  <i class="fa-solid fa-times"></i>
                </button>
              </div>
              <div class="logo-placeholder" *ngIf="!editForm.logoURL || logoPreviewError">
                <i class="fa-solid fa-image"></i>
                <span>No logo</span>
              </div>
              <!-- Input options -->
              <div class="logo-input-options">
                <div class="logo-url-input">
                  <input
                    type="text"
                    [(ngModel)]="editForm.logoURL"
                    placeholder="Enter image URL or upload a file..."
                    [disabled]="isSaving"
                    (input)="logoPreviewError = false"
                  />
                </div>
                <div class="logo-upload">
                  <label class="upload-btn" [class.disabled]="isSaving">
                    <i class="fa-solid fa-upload"></i>
                    Upload
                    <input
                      type="file"
                      accept="image/*"
                      (change)="onLogoFileSelected($event)"
                      [disabled]="isSaving"
                      style="display: none"
                    />
                  </label>
                </div>
              </div>
              <span class="form-hint">Upload an image (max 500KB) or enter an external URL</span>
            </div>
          </div>
        </div>
        <div class="form-footer">
          <button class="save-btn" (click)="saveOrganization()" [disabled]="isSaving || !editForm.name.trim()">
            <i class="fa-solid fa-check"></i>
            {{ editForm.parentId ? 'Create Sub-Organization' : 'Create Organization' }}
          </button>
          <button class="cancel-btn" (click)="cancelEdit()" [disabled]="isSaving">
            Cancel
          </button>
        </div>
      </div>

      <!-- Edit Form -->
      <div class="edit-form" *ngIf="isEditing && selectedOrganization">
        <div class="form-header">
          <h2><i class="fa-solid fa-edit"></i> Edit Organization</h2>
        </div>
        <div class="form-body">
          <div class="form-group">
            <label>Name <span class="required">*</span></label>
            <input
              type="text"
              [(ngModel)]="editForm.name"
              placeholder="Organization name"
              [disabled]="isSaving"
            />
          </div>
          <div class="form-group">
            <label>Description</label>
            <textarea
              [(ngModel)]="editForm.description"
              placeholder="Brief description of the organization..."
              rows="4"
              [disabled]="isSaving"
            ></textarea>
          </div>
          <div class="form-group">
            <label>Parent Organization</label>
            <select [(ngModel)]="editForm.parentId" [disabled]="isSaving" class="parent-select">
              <option [ngValue]="null">None (top-level organization)</option>
              <optgroup label="Available Parents">
                <option *ngFor="let org of getPotentialParents()" [ngValue]="org.ID">
                  {{ getOrgPathString(org) }}
                </option>
              </optgroup>
            </select>
          </div>
          <div class="form-group">
            <label>Logo</label>
            <div class="logo-input-group">
              <!-- Logo Preview -->
              <div class="logo-preview" *ngIf="editForm.logoURL && !logoPreviewError">
                <img [src]="editForm.logoURL" alt="Logo preview" (error)="onLogoPreviewError()">
                <button class="clear-logo-btn" type="button" (click)="clearLogo()" title="Remove logo">
                  <i class="fa-solid fa-times"></i>
                </button>
              </div>
              <div class="logo-placeholder" *ngIf="!editForm.logoURL || logoPreviewError">
                <i class="fa-solid fa-image"></i>
                <span>No logo</span>
              </div>
              <!-- Input options -->
              <div class="logo-input-options">
                <div class="logo-url-input">
                  <input
                    type="text"
                    [(ngModel)]="editForm.logoURL"
                    placeholder="Enter image URL or upload a file..."
                    [disabled]="isSaving"
                    (input)="logoPreviewError = false"
                  />
                </div>
                <div class="logo-upload">
                  <label class="upload-btn" [class.disabled]="isSaving">
                    <i class="fa-solid fa-upload"></i>
                    Upload
                    <input
                      type="file"
                      accept="image/*"
                      (change)="onLogoFileSelected($event)"
                      [disabled]="isSaving"
                      style="display: none"
                    />
                  </label>
                </div>
              </div>
              <span class="form-hint">Upload an image (max 500KB) or enter an external URL</span>
            </div>
          </div>
        </div>
        <div class="form-footer">
          <button class="save-btn" (click)="saveOrganization()" [disabled]="isSaving || !editForm.name.trim()">
            <i class="fa-solid fa-check"></i>
            Save Changes
          </button>
          <button class="cancel-btn" (click)="cancelEdit()" [disabled]="isSaving">
            Cancel
          </button>
        </div>
      </div>

      <!-- View Details -->
      <div class="org-details" *ngIf="selectedOrganization && !isEditing && !isCreating">
        <!-- Breadcrumb path -->
        <div class="org-breadcrumb" *ngIf="getOrgPath(selectedOrganization).length > 1">
          <ng-container *ngFor="let pathOrg of getOrgPath(selectedOrganization); let last = last">
            <span
              class="breadcrumb-item"
              [class.current]="last"
              (click)="!last && selectOrganization(pathOrg)"
            >
              {{ pathOrg.Name }}
            </span>
            <i class="fa-solid fa-chevron-right breadcrumb-sep" *ngIf="!last"></i>
          </ng-container>
        </div>

        <div class="details-header">
          <div class="org-avatar large" *ngIf="!getLogoURL(selectedOrganization)">
            {{ getInitials(selectedOrganization.Name) }}
          </div>
          <div class="org-logo large" *ngIf="getLogoURL(selectedOrganization)">
            <img [src]="getLogoURL(selectedOrganization)" [alt]="selectedOrganization.Name + ' logo'">
          </div>
          <div class="org-title">
            <h2>{{ selectedOrganization.Name }}</h2>
            <span *ngIf="selectedOrganization.Status" class="status-badge">
              {{ selectedOrganization.Status }}
            </span>
          </div>
        </div>

        <div class="details-body">
          <div class="detail-section" *ngIf="selectedOrganization.Description">
            <h4>Description</h4>
            <p>{{ selectedOrganization.Description }}</p>
          </div>

          <!-- Hierarchy info -->
          <div class="detail-section hierarchy-section">
            <h4>Hierarchy</h4>
            <div class="hierarchy-info">
              <div class="hierarchy-item" *ngIf="getParentName(selectedOrganization)">
                <span class="hierarchy-label">Parent:</span>
                <span class="hierarchy-value clickable" (click)="navigateToParent()">
                  <i class="fa-solid fa-level-up-alt"></i>
                  {{ getParentName(selectedOrganization) }}
                </span>
              </div>
              <div class="hierarchy-item" *ngIf="!getParentName(selectedOrganization)">
                <span class="hierarchy-label">Level:</span>
                <span class="hierarchy-value">
                  <i class="fa-solid fa-crown"></i>
                  Top-level organization
                </span>
              </div>
              <div class="hierarchy-item">
                <span class="hierarchy-label">Depth:</span>
                <span class="hierarchy-value">{{ getOrgPath(selectedOrganization).length }}</span>
              </div>
            </div>
          </div>

          <!-- Plan assignment (restricted): sets Subscription -> PlanPrice -> Plan + OrganizationConfig.PlanID -->
          <div class="detail-section plan-section">
            <h4>Plan</h4>
            <p class="current-plan">
              Current:
              <strong>{{ currentPlan ? currentPlan.Name : 'None' }}</strong>
            </p>
            <div class="plan-assign">
              <select [(ngModel)]="selectedPlanId" [disabled]="isSavingPlan" class="plan-select">
                <option [ngValue]="null">Select a plan…</option>
                <option *ngFor="let p of plans" [ngValue]="p.ID">{{ p.Name }}</option>
              </select>
              <button
                class="action-btn"
                (click)="setPlan()"
                [disabled]="isSavingPlan || !selectedPlanId || selectedPlanId === currentPlan?.ID"
              >
                <i class="fa-solid" [class.fa-spinner]="isSavingPlan" [class.fa-spin]="isSavingPlan" [class.fa-check]="!isSavingPlan"></i>
                {{ isSavingPlan ? 'Setting…' : 'Set Plan' }}
              </button>
            </div>
            <p class="plan-message" *ngIf="planMessage">{{ planMessage }}</p>
          </div>

          <div class="detail-section">
            <h4>Quick Actions</h4>
            <div class="action-buttons">
              <button class="action-btn" (click)="startCreateSubOrg()">
                <i class="fa-solid fa-plus"></i>
                Add Sub-Organization
              </button>
              <button class="action-btn" (click)="navigateToSettings()">
                <i class="fa-solid fa-sliders"></i>
                Configure Settings
              </button>
              <button class="action-btn" (click)="openOrganizationRecord()">
                <i class="fa-solid fa-external-link-alt"></i>
                Open Full Record
              </button>
            </div>
          </div>
        </div>

        <div class="details-footer">
          <button class="edit-btn" (click)="startEdit()" [disabled]="isSaving">
            <i class="fa-solid fa-edit"></i>
            Edit
          </button>
          <button class="delete-btn" (click)="deleteOrganization()" [disabled]="isSaving">
            <i class="fa-solid fa-trash"></i>
            Delete
          </button>
        </div>
      </div>

      <!-- No Selection -->
      <div class="no-selection" *ngIf="!selectedOrganization && !isCreating">
        <i class="fa-solid fa-building"></i>
        <p>Select an organization to view details</p>
        <p class="hint">or create a new one</p>
      </div>
    </div>
  </div>
</div>

<!-- Confirm Dialog -->
<div class="confirm-overlay" *ngIf="confirmDialogVisible" (click)="closeConfirmDialog()">
  <div class="confirm-dialog" (click)="$event.stopPropagation()">
    <div class="confirm-header">
      <i class="fa-solid fa-exclamation-triangle"></i>
      <h3>{{ confirmDialogTitle }}</h3>
    </div>
    <div class="confirm-body">
      <p>{{ confirmDialogMessage }}</p>
    </div>
    <div class="confirm-actions">
      <button class="confirm-btn-yes danger" (click)="confirmDialogYes()" [disabled]="isSaving">
        <i class="fa-solid fa-trash"></i> Yes, Delete
      </button>
      <button class="confirm-btn-no" (click)="closeConfirmDialog()" [disabled]="isSaving">
        <i class="fa-solid fa-times"></i> Cancel
      </button>
    </div>
  </div>
</div>
